// Must be the first import: it patches `fetch` so every `/api/**` call is
// answered in-process before any module can fire one. Demo build — no backend.
import '~/demo/api/install';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';

import { routeTree } from './routeTree.gen';
import { AuthProvider, useAuth } from '~/lib/auth';
import { AppErrorBoundary } from '~/components/ErrorState';
import { reportError } from '~/lib/api/errors';
import './styles/globals.css';

// ── Global client-error capture → admin Error Log ───────────────────────────
// Surface uncaught client errors (and unhandled promise rejections) into the
// same `error_log` the server writes to, via POST /api/errors/log. Without this
// the admin Error Log only ever shows server-side rows.
//
// Guardrails so an error loop can't spam the endpoint:
//   • cap to CLIENT_ERROR_CAP reports per page session, and
//   • dedupe identical (message + url) signatures within a short window.
const CLIENT_ERROR_CAP = 25;
const DEDUPE_WINDOW_MS = 10_000;
let clientErrorCount = 0;
const recentSignatures = new Map<string, number>();

function captureClientError(message: string, stack: string | undefined) {
  const msg = (message || '').trim();
  if (!msg) return;
  if (clientErrorCount >= CLIENT_ERROR_CAP) return;

  const url = window.location.href;
  const sig = `${msg}::${url}`;
  const now = Date.now();
  const last = recentSignatures.get(sig);
  if (last != null && now - last < DEDUPE_WINDOW_MS) return;
  recentSignatures.set(sig, now);

  clientErrorCount += 1;
  // Best-effort; reportError swallows its own failures so this never re-throws.
  void reportError({
    source: 'client',
    severity: 'error',
    message: msg.slice(0, 2000),
    context: { stack: stack?.slice(0, 8000) ?? null, url, userAgent: navigator.userAgent },
  });
}

window.addEventListener('error', (event: ErrorEvent) => {
  const err = event.error as Error | undefined;
  captureClientError(err?.message || event.message, err?.stack);
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason = event.reason as unknown;
  if (reason instanceof Error) {
    captureClientError(reason.message, reason.stack);
  } else {
    captureClientError(`Unhandled rejection: ${String(reason)}`, undefined);
  }
});

// Recover from stale chunks after a deploy. When a new build ships, hashed chunk
// filenames change; a tab still on the old build fails to lazy-load a route
// ("Failed to fetch dynamically imported module"). Reload once to pull the fresh
// index + chunks. The 10s throttle prevents a reload loop when a chunk is truly
// missing (the error boundary shows instead).
window.addEventListener('vite:preloadError', () => {
  const KEY = 'pdq-preload-reloaded-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

const queryClient = new QueryClient({
  // Surface query failures (don't let components silently render empty on error).
  queryCache: new QueryCache({
    onError: (error, query) => console.error('[query error]', query.queryKey, error),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient, auth: undefined! },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth();
  // Block the router from mounting until AuthProvider has hydrated the
  // persisted session. Without this gate, the route guard runs once with
  // a null session and redirects valid users to /login on every refresh.
  if (auth.loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: '#F4F8F8', color: '#0F1E2E', fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{ fontSize: 14, color: '#6B7A8C' }}>Loading…</div>
      </div>
    );
  }
  return <RouterProvider router={router} context={{ auth }} />;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');
createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
      {import.meta.env.DEV && (
        <>
          <ReactQueryDevtools buttonPosition="bottom-right" />
          <TanStackRouterDevtools router={router} position="bottom-left" />
        </>
      )}
    </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
